import { useEffect, useState } from "react";
import API from "../api";
import { FilePicker } from "@capawesome/capacitor-file-picker";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { FileViewer } from "@capacitor/file-viewer";
import bgTemple from "../assets/bg-temple.png";

function Planches() {
  const [planches, setPlanches] = useState([]);
  const [filter, setFilter] = useState("tous");
  const [title, setTitle] = useState("");
  const [grade, setGrade] = useState("apprenti");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user.role === "admin";

  useEffect(() => {
    fetchPlanches();
  }, []);

  const fetchPlanches = async () => {
    try {
      const res = await API.get("/planches");
      setPlanches(res.data);
    } catch (err) {
      console.error("Erreur planches:", err);
    }
  };

  const pickPdfFile = async () => {
    try {
      const result = await FilePicker.pickFiles({
        types: ["application/pdf"],
        multiple: false,
        readData: true,
      });

      const picked = result.files?.[0];

      if (!picked?.data) {
        alert("Impossible de lire le PDF");
        return;
      }

      const byteCharacters = atob(picked.data);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const blob = new Blob([new Uint8Array(byteNumbers)], {
        type: picked.mimeType || "application/pdf",
      });

      const selectedFile = new File(
        [blob],
        picked.name || "planche.pdf",
        {
          type: picked.mimeType || "application/pdf",
        }
      );

      setFile(selectedFile);
    } catch (err) {
      console.error(err);
      alert("Impossible de choisir le fichier PDF");
    }
  };

  const uploadPlanche = async () => {
    if (!title.trim()) {
      alert("Titre obligatoire");
      return;
    }

    if (!file) {
      alert("Fichier PDF obligatoire");
      return;
    }

    const formData = new FormData();

    formData.append("title", title.trim());
    formData.append("grade", grade);
    formData.append("pdf", file);

    try {
      setLoading(true);

      await API.post("/planches", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setTitle("");
      setGrade("apprenti");
      setFile(null);

      await fetchPlanches();

      alert("Planche ajoutée ✅");
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erreur upload");
    } finally {
      setLoading(false);
    }
  };

  const deletePlanche = async (id) => {
    if (!window.confirm("Supprimer cette planche ?")) {
      return;
    }

    try {
      await API.delete(`/planches/${id}`);
      await fetchPlanches();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erreur suppression");
    }
  };

  const openPdf = async (url) => {
    try {
      if (!url) {
        alert("Fichier introuvable");
        return;
      }

      const finalUrl = url.startsWith("http")
        ? url
        : `https://api.joss-serveur.duckdns.org${url}`;

      const response = await fetch(finalUrl);

      const blob = await response.blob();

      const reader = new FileReader();

      reader.onloadend = async () => {
        try {
          const base64data = reader.result.split(",")[1];

          const fileName = `${Date.now()}.pdf`;

          const savedFile = await Filesystem.writeFile({
            path: fileName,
            data: base64data,
            directory: Directory.Cache,
          });

          await FileViewer.openDocumentFromLocalPath({
            path: savedFile.uri,
          });
        } catch (err) {
          console.error(err);
          alert("Impossible d'ouvrir le PDF");
        }
      };

      reader.readAsDataURL(blob);
    } catch (err) {
      console.error(err);
      alert("Impossible d'ouvrir le PDF");
    }
  };

  const formatGrade = (g) => {
    if (g === "maitre") return "Maître";
    if (g === "compagnon") return "Compagnon";
    return "Apprenti";
  };

  const filteredPlanches =
    filter === "tous"
      ? planches
      : planches.filter((p) => p.grade === filter);

  return (
    <div style={styles.page}>
      <div style={styles.overlay} />

      <div style={styles.content}>
        <div style={styles.header}>
          <div>
            <div style={styles.kicker}>Bibliothèque</div>

            <h1 style={styles.title}>Planches</h1>

            <div style={styles.subtitle}>
              Travaux, lectures et archives
            </div>
          </div>
        </div>

        {isAdmin && (
          <div style={styles.uploadBox}>
            <div style={styles.uploadTitle}>
              Ajouter une planche
            </div>

            <input
              style={styles.input}
              type="text"
              placeholder="Titre de la planche"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <select
              style={styles.input}
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
            >
              <option value="apprenti">Apprenti</option>
              <option value="compagnon">Compagnon</option>
              <option value="maitre">Maître</option>
            </select>

            <button
              type="button"
              style={styles.fileButton}
              onClick={pickPdfFile}
              disabled={loading}
            >
              {file
                ? `📄 ${file.name}`
                : "Choisir un fichier PDF"}
            </button>

            <button
              type="button"
              style={styles.uploadButton}
              onClick={uploadPlanche}
              disabled={loading}
            >
              {loading
                ? "Envoi..."
                : "Ajouter la planche"}
            </button>
          </div>
        )}

        <div style={styles.filters}>
          {["tous", "apprenti", "compagnon", "maitre"].map((f) => (
            <button
              type="button"
              key={f}
              onClick={() => setFilter(f)}
              style={{
                ...styles.filterButton,
                ...(filter === f
                  ? styles.filterActive
                  : {}),
              }}
            >
              {f === "tous"
                ? "Tous"
                : formatGrade(f)}
            </button>
          ))}
        </div>

        {filteredPlanches.length === 0 && (
          <div style={styles.empty}>
            Aucune planche disponible
          </div>
        )}

        <div style={styles.list}>
          {filteredPlanches.map((p) => (
            <div key={p.id} style={styles.card}>
              <div style={styles.cardTop}>
                <div style={styles.docIcon}>📜</div>

                <div style={styles.cardText}>
                  <h3 style={styles.cardTitle}>
                    {p.title}
                  </h3>

                  <div style={styles.metaRow}>
                    <span style={styles.gradeBadge}>
                      {formatGrade(p.grade)}
                    </span>

                    <span style={styles.pdfInfo}>
                      PDF
                    </span>
                  </div>
                </div>
              </div>

              <div style={styles.actions}>
                <button
                  type="button"
                  style={styles.readButton}
                  onClick={() => openPdf(p.file_url)}
                >
                  Lire
                </button>

                <a
                  href={p.file_url}
                  download
                  style={styles.downloadButton}
                >
                  Télécharger
                </a>

                {isAdmin && (
                  <button
                    type="button"
                    style={styles.deleteButton}
                    onClick={() => deletePlanche(p.id)}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const gold = "#d6b56d";

const styles = {
  page: {
    minHeight: "100vh",
    position: "relative",
    overflowX: "hidden",
    backgroundImage: `url(${bgTemple})`,
    backgroundSize: "cover",
    backgroundPosition: "center top",
    color: "#f8f4ec",
    fontFamily: "Arial, sans-serif",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background:
      "linear-gradient(180deg, rgba(3,7,14,0.42) 0%, rgba(3,7,14,0.84) 45%, rgba(3,7,14,0.97) 100%)",
    zIndex: 0,
  },

  content: {
    position: "relative",
    zIndex: 1,
    padding: "24px 18px 125px",
  },

  header: {
    marginBottom: "18px",
  },

  kicker: {
    color: gold,
    fontSize: "16px",
    fontWeight: "900",
  },

  title: {
    margin: 0,
    color: "#ffffff",
    fontSize: "42px",
    lineHeight: "44px",
    fontWeight: "900",
    letterSpacing: "-1px",
  },

  subtitle: {
    marginTop: "6px",
    color: "rgba(255,255,255,0.72)",
    fontSize: "17px",
  },

  uploadBox: {
    marginBottom: "18px",
    padding: "15px",
    borderRadius: "20px",
    background: "rgba(1,6,13,0.72)",
    border: "1px solid rgba(214,181,109,0.42)",
    boxShadow: "0 14px 34px rgba(0,0,0,0.38)",
    backdropFilter: "blur(12px)",
  },

  uploadTitle: {
    color: gold,
    fontSize: "18px",
    fontWeight: "900",
    marginBottom: "12px",
  },

  input: {
    width: "100%",
    padding: "13px",
    marginBottom: "10px",
    borderRadius: "13px",
    border: "1px solid rgba(214,181,109,0.28)",
    background: "rgba(255,255,255,0.08)",
    color: "#ffffff",
    fontSize: "15px",
    boxSizing: "border-box",
    outline: "none",
  },

  fileButton: {
    width: "100%",
    padding: "14px",
    marginBottom: "12px",
    borderRadius: "14px",
    border: "1px solid rgba(214,181,109,0.45)",
    background: "rgba(0,0,0,0.35)",
    color: gold,
    fontSize: "15px",
    fontWeight: "900",
    textAlign: "center",
  },

  uploadButton: {
    width: "100%",
    padding: "14px",
    borderRadius: "14px",
    border: "none",
    background: gold,
    color: "#060606",
    fontSize: "15px",
    fontWeight: "900",
  },

  filters: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "8px",
    marginBottom: "18px",
  },

  filterButton: {
    padding: "10px 6px",
    borderRadius: "14px",
    border: "1px solid rgba(214,181,109,0.26)",
    background: "rgba(0,0,0,0.34)",
    color: "rgba(255,255,255,0.72)",
    fontSize: "12px",
    fontWeight: "900",
  },

  filterActive: {
    background: gold,
    color: "#050505",
    border: "1px solid rgba(255,255,255,0.1)",
  },

  empty: {
    padding: "24px",
    textAlign: "center",
    borderRadius: "20px",
    background: "rgba(1,6,13,0.68)",
    border: "1px solid rgba(214,181,109,0.28)",
    color: "rgba(255,255,255,0.7)",
  },

  list: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  card: {
    padding: "14px",
    borderRadius: "18px",
    background:
      "linear-gradient(180deg, rgba(2,8,17,0.9), rgba(0,3,9,0.92))",
    border: "1px solid rgba(214,181,109,0.36)",
    boxShadow:
      "inset 0 0 22px rgba(255,255,255,0.03), 0 12px 28px rgba(0,0,0,0.38)",
    backdropFilter: "blur(12px)",
  },

  cardTop: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "14px",
  },

  docIcon: {
    width: "54px",
    height: "54px",
    minWidth: "54px",
    borderRadius: "16px",
    border: "1px solid rgba(214,181,109,0.55)",
    background:
      "radial-gradient(circle at center, rgba(214,181,109,0.16), rgba(0,0,0,0.65))",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "27px",
  },

  cardText: {
    flex: 1,
    minWidth: 0,
  },

  cardTitle: {
    margin: "0 0 7px",
    color: "#ffffff",
    fontSize: "20px",
    fontWeight: "900",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  metaRow: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
  },

  gradeBadge: {
    padding: "5px 10px",
    borderRadius: "999px",
    border: "1px solid rgba(214,181,109,0.45)",
    background: "rgba(214,181,109,0.12)",
    color: gold,
    fontSize: "13px",
    fontWeight: "900",
  },

  pdfInfo: {
    color: "rgba(255,255,255,0.5)",
    fontSize: "13px",
    fontWeight: "800",
  },

  actions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 42px",
    gap: "8px",
  },

  readButton: {
    height: "44px",
    borderRadius: "13px",
    border: "none",
    background: gold,
    color: "#050505",
    fontSize: "14px",
    fontWeight: "900",
  },

  downloadButton: {
    height: "44px",
    borderRadius: "13px",
    border: "1px solid rgba(214,181,109,0.28)",
    background: "rgba(0,0,0,0.32)",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "900",
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  deleteButton: {
    height: "44px",
    borderRadius: "13px",
    border: "1px solid rgba(255,90,90,0.45)",
    background: "rgba(130,20,20,0.75)",
    color: "#ffffff",
    fontSize: "25px",
    fontWeight: "900",
  },
};

export default Planches;